import { CircularProgress, Backdrop } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes, useNavigate } from 'react-router-dom';
import CommonLayout from './components/CommonLayout';
import { getDocsAWS, logout } from './offchain-logic/services';
import { logoutWithCookies, checkAuthStatus, refreshTokenWithCookie } from './services/authService';
import { DocumentList } from './pages/DocumentList';
import Enroll from './pages/Enroll';
import Login from './pages/Login';
import { UpdateDocsVersion } from './pages/UpdateDocsVersion';
import { UploadDocs } from './pages/UploadDocs';
import Toast from './components/Toast';
import { RepoDocument } from './common/types';
import './App.css';
import { AddActivity } from './pages/AddActivity';
import PaymentSuccess from './pages/PaymentSuccess';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SinglePaymentSuccessPage from './pages/SinglePaymentSuccessPage';
import { WorksList } from './pages/Works/WorksList';
import { CreateWork } from './pages/Works/CreateWork';
import { WorkDetail } from './pages/Works/WorkDetail';
import { Dashboard } from './pages/Dashboard';
import { DocumentFolder } from './pages/DocumentFolder/DocumentFolder';
import { AdminDashboard } from './pages/Admin/AdminDashboard';
import { AdminPayments } from './pages/Admin/AdminPayments';
import AdminSupport from './pages/Admin/AdminSupport';
import AdminNotifications from './pages/Admin/AdminNotifications';
import CentralizedDialog from './pages/component/CentralizedDialog';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ComingSoon from './pages/ComingSoon';
import Profile from './pages/Profile';
import Support from './pages/Support';

function App() {
  const [documentList, setDocumentList] = useState<RepoDocument[]>([]);
  const [documentChange, setDocumentChange] = useState<boolean>(false);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState<boolean | null>(null); // null = checking
  const [userAuthenticated, setUserAuthenticated] = useState<string>("");
  const [userRole, setUserRole] = useState<string>("");
  
  // Stato per dialog sessione scaduta
  const [sessionExpiredDialogOpen, setSessionExpiredDialogOpen] = useState<boolean>(false);
  
  // Stato per loader globale API
  const [apiLoadingCount, setApiLoadingCount] = useState<number>(0);

  // La chiave viene iniettata dalla configurazione dell'ambiente di build.
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');
  

  const theme = createTheme({
    palette: {
      primary: {
        main: '#20679F',
      },
      secondary: {
        main: '#00A7D9'
      }
    },
    typography: {
      fontFamily: [
        '"Jost"',
        'sans-serif',
      ].join(','),
    }
  });

  const handleLogout = async () => {
    try {
      // Logout con cookie HttpOnly
      await logoutWithCookies();
      console.log("✅ Logout completato");
    } catch (error) {
      console.error("⚠️ Errore logout:", error);
    } finally {
      // Pulisci sempre sessionStorage e stato locale
      sessionStorage.clear();
      setIsUserAuthenticated(false);
    }
  };

  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

  // Listener per evento sessione scaduta
  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      console.log('🚨 Evento sessione scaduta ricevuto');
      setSessionExpiredDialogOpen(true);
      setIsUserAuthenticated(false);
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  // Listener per eventi caricamento API
  useEffect(() => {
    const handleLoadingStart = () => {
      setApiLoadingCount(prev => prev + 1);
    };

    const handleLoadingEnd = () => {
      setApiLoadingCount(prev => Math.max(0, prev - 1));
    };

    window.addEventListener('api:loading:start', handleLoadingStart);
    window.addEventListener('api:loading:end', handleLoadingEnd);

    return () => {
      window.removeEventListener('api:loading:start', handleLoadingStart);
      window.removeEventListener('api:loading:end', handleLoadingEnd);
    };
  }, []);

  // Handler per redirect a login dopo conferma dialog
  const handleSessionExpiredConfirm = () => {
    setSessionExpiredDialogOpen(false);
    window.location.href = '/login';
  };



  // 🛑 3. NUOVO COMPONENTE WRAPPER PER RISOLVERE L'ERRORE DI CONTESTO 🛑
const StatusPageWrapper: React.FC<{ onFinalSuccess: () => void }> = ({ onFinalSuccess }) => {
    const navigate = useNavigate();
    
    // Recupera il client_secret dall'URL
    const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret');
    
    //const options = clientSecret ? { clientSecret, appearance: { theme: 'stripe' } } : {};

      const options: StripeElementsOptions = clientSecret
          ? {
              appearance: { 
                  theme: 'stripe', // Ora TypeScript lo accetta come valore letterale consentito
              },
              locale: 'it', // Assicurati di avere StripeElementsOptions importato
          }
          : {
              // Se clientSecret è vuoto, usa un oggetto vuoto, che è accettato come opzione valida
              // Per maggiore sicurezza, puoi anche rendere opzionale il clientSecret
          };

    if (!clientSecret) {
        // Se il clientSecret è mancante (errore di reindirizzamento/URL), naviga via
        navigate('/'); 
        return null;
    }
    
    return (
        // Avvolgi la pagina di stato con <Elements>
        <Elements stripe={stripePromise} options={options}>
            <PaymentSuccess onFinalSuccess={onFinalSuccess} /> 
        </Elements>
    );
};

  const handleOpenToast = (message: string, severity: 'success' | 'error') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setOpenToast(true);
  };

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  const onFinalSuccess = () => {
    console.log("onFinalSuccess");
  }

  const handleLogin = (_auth: any, _username: string, _userInfo?: any) => {
    // I token sono gestiti automaticamente dai cookie HttpOnly
    // Non salviamo più token in sessionStorage per sicurezza
    console.log('✅ Login con httpOnly cookies');

    // Estrai il ruolo dai Cognito groups se presenti
    const groups = _userInfo?.['cognito:groups'] || [];
    const isAdmin = groups.includes('Admin');
    const role = isAdmin ? 'Admin' : 'User';
    
    // Salva le informazioni utente complete (sempre necessario)
    if (_userInfo) {
      const userToSave = {
        sub: _userInfo.sub,
        email: _userInfo.email,
        email_verified: _userInfo.email_verified,
        phone_number: _userInfo.phone_number,
        ragione_sociale: _userInfo['custom:ragione_sociale'],
        partita_iva: _userInfo['custom:partita_iva'],
        codice_fiscale: _userInfo['custom:codice_fiscale'],
        pec: _userInfo['custom:pec'],
        role: role
      };
      sessionStorage.setItem('user', JSON.stringify(userToSave));
      console.log('✅ Informazioni utente salvate:', userToSave);
    } else {
      // Fallback: salva solo l'email se userInfo non disponibile
      sessionStorage.setItem('user', JSON.stringify({ email: _username, role: role }));
    }

    setIsUserAuthenticated(true);
    setUserAuthenticated(_username);
    setUserRole(role);
    console.log('👤 User role set to:', role, 'Groups:', groups);
    
    // Usa ragione sociale se disponibile, altrimenti email
    const displayName = _userInfo?.['custom:ragione_sociale'] || _username;
    handleOpenToast("Benvenuto, " + displayName, 'success');
  };

  useEffect(() => {
    if (documentChange === true) {
      getDocsAWS((err, res) => {
        console.log('📄 [DOCS] Risposta getDocsAWS:', res);
        if(err !== null || res === null){
          return;
        }

        // La nuova API ritorna direttamente l'array di documenti
        const documents = res.documents || res.data?.documents || res;
        setDocumentList(Array.isArray(documents) ? documents : []);
        setDocumentChange(false);
      });
    }
  }, [documentChange]);

  // Verifica autenticazione dai cookie httpOnly al mount
  useEffect(() => {
    console.log('🔍 [AUTH CHECK] Inizio verifica autenticazione...');
    const checkAuth = async () => {
      try {
        const status = await checkAuthStatus();
        console.log('🔍 [AUTH CHECK] Risposta checkAuthStatus:', status);
        if (status.authenticated && status.user) {
          // Utente autenticato, salva info in sessionStorage e stato
          sessionStorage.setItem('user', JSON.stringify(status.user));
          setIsUserAuthenticated(true);
          setUserAuthenticated(status.user.email);
          
          // Calcola il ruolo dai cognito:groups
          const groups = status.user['cognito:groups'] || [];
          const isAdmin = groups.includes('Admin');
          const role = isAdmin ? 'Admin' : 'User';
          
          setUserRole(role);
          console.log('✅ [AUTH CHECK] Ruolo calcolato da groups:', role, 'Groups:', groups);
          console.log('✅ [AUTH CHECK] Utente autenticato dai cookie:', status.user.email);
        } else {
          console.log('❌ [AUTH CHECK] Utente NON autenticato');
          setIsUserAuthenticated(false);
        }
      } catch (error) {
        console.log('❌ [AUTH CHECK] Verifica auth fallita:', error);
        setIsUserAuthenticated(false);
      }
    };
    
    checkAuth();
  }, []);
  
  // ============================================================================
  // REFRESH PROATTIVO TOKEN - Ogni 50 minuti
  // ============================================================================
  // I token Cognito hanno una durata di 1 ora. Per evitare che l'utente 
  // riceva errori 401 durante l'uso normale dell'app, refreshiamo i token
  // preventivamente ogni 50 minuti (10 minuti prima della scadenza).
  // Questo garantisce una sessione fluida fino a 30 giorni (durata RefreshToken).
  // ============================================================================
  useEffect(() => {
    if (isUserAuthenticated === true) {
      console.log('⏰ [REFRESH PROATTIVO] Timer attivato - refresh ogni 50 minuti');
      
      const refreshInterval = setInterval(async () => {
        try {
          console.log('🔄 [REFRESH PROATTIVO] Inizio refresh preventivo token...');
          await refreshTokenWithCookie();
          console.log('✅ [REFRESH PROATTIVO] Token refreshati con successo');
        } catch (error) {
          console.error('❌ [REFRESH PROATTIVO] Errore durante refresh:', error);
          // Se il rinnovo fallisce, la sessione non è più affidabile.
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
      }, 50 * 60 * 1000); // 50 minuti in millisecondi
      
      return () => {
        console.log('🛑 [REFRESH PROATTIVO] Timer disattivato');
        clearInterval(refreshInterval);
      };
    }
  }, [isUserAuthenticated]);
  
  useEffect(() => {
    console.log('🔍 [REDIRECT CHECK] isUserAuthenticated:', isUserAuthenticated, 'pathname:', window.location.pathname);
    
    // Whitelist pagine accessibili senza autenticazione
    const publicRoutes = ['/login', '/enroll', '/payment-success', '/subscription-success', '/coming-soon'];
    const isPublicRoute = window.location.pathname === '/' || publicRoutes.some(route => window.location.pathname.startsWith(route));
    
    console.log('🔍 [REDIRECT CHECK] isPublicRoute:', isPublicRoute);
    
    // Se ancora in fase di check (null), NON redirigere - aspetta il check
    if (isUserAuthenticated === null) {
      console.log('⏳ [REDIRECT CHECK] Stato null - aspetto completamento check auth');
      return; // Aspetta il completamento del check auth
    }
    
    // Solo se esplicitamente false (non autenticato) E non su rotta pubblica, redirect
    if (isUserAuthenticated === false && !isPublicRoute) {
      console.log('🚨 [REDIRECT CHECK] Redirect a /login - utente non autenticato su rotta protetta');
      window.location.href = '/login';
    } else {
      console.log('✅ [REDIRECT CHECK] Nessun redirect necessario');
    }
  }, [isUserAuthenticated]);

  return (
    <ThemeProvider theme={theme}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login onLogin={handleLogin} handleOpenToast={handleOpenToast} onLogout={handleLogout} setDocumentChange={setDocumentChange} />} />
            <Route path="/enroll" element={<Enroll onLogin={handleLogin} handleOpenToast={handleOpenToast} />} />
            <Route path="/payment-success" element={<SinglePaymentSuccessPage  />} />
            <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
            <Route path="/coming-soon" element={<ComingSoon />} />
            <Route path="/" element={
              (() => {
                if (window.location.pathname === '/') {
                  return <ComingSoon />;
                }
                console.log('🔍 [ROUTER] Evaluating protected route - isUserAuthenticated:', isUserAuthenticated);
                if (isUserAuthenticated === null) {
                  console.log('⏳ [ROUTER] Stato null - mostro loader');
                  return <div className='loader-container'><CircularProgress /></div>;
                }
                if (isUserAuthenticated) {
                  console.log('✅ [ROUTER] Utente autenticato - mostro CommonLayout');
                  return <CommonLayout onLogout={handleLogout} user={userAuthenticated} userRole={userRole} userAuthenticated={userAuthenticated}><Outlet /></CommonLayout>;
                }
                console.log('🚨 [ROUTER] Utente non autenticato - Navigate a /login');
                return <Navigate replace to={"/login"} />;
              })()
            }>
              <Route path="/documents" element={<DocumentFolder />} />
              <Route path="/upload-docs" element={<UploadDocs setDocumentChange={setDocumentChange} userAuthenticated={userAuthenticated} handleOpenToast={handleOpenToast} />} />
              <Route path="/documents/update-version" element={<UpdateDocsVersion setDocumentChange={setDocumentChange} userAuthenticated={userAuthenticated} />} />
              <Route path="/add-planning" element={<AddActivity handleOpenToast={handleOpenToast} />} />
              <Route path="/dashboard" element={<Dashboard handleOpenToast={handleOpenToast} documentList={documentList} />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/support" element={<Support />} />
              <Route path="/works" element={<WorksList handleOpenToast={handleOpenToast} />} />
              <Route path="/create-work" element={<CreateWork handleOpenToast={handleOpenToast} />} />
              <Route path="/work-detail/:id_work" element={<WorkDetail handleOpenToast={handleOpenToast} />} />
              <Route path="/admin-dashboard" element={
                userRole === 'Admin' ? (
                  <AdminDashboard />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              } />
              <Route path="/admin-payments" element={
                userRole === 'Admin' ? (
                  <AdminPayments />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              } />
              <Route path="/admin-support" element={
                userRole === 'Admin' ? (
                  <AdminSupport />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              } />
              <Route path="/admin-notifications" element={
                userRole === 'Admin' ? (
                  <AdminNotifications />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              } />
              {/* default route */}
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Route>
          </Routes>
          <Toast
            open={openToast}
            message={toastMessage}
            severity={toastSeverity}
            onClose={handleCloseToast}
          />
          
          {/* Dialog Sessione Scaduta */}
          <CentralizedDialog
            open={sessionExpiredDialogOpen}
            onClose={() => setSessionExpiredDialogOpen(false)}
            title="Sessione Scaduta"
            icon={<ErrorOutlineIcon />}
            iconColor="#d32f2f"
            primaryAction={{
              label: "OK",
              onClick: handleSessionExpiredConfirm
            }}
          >
            La tua sessione è scaduta. Per continuare ad utilizzare l'applicazione, effettua nuovamente il login.
          </CentralizedDialog>
        </div>
      </Router>
      
      {/* Loader globale per chiamate API */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.3)'
        }}
        open={apiLoadingCount > 0}
      >
        <CircularProgress color="inherit" size={60} thickness={4} />
      </Backdrop>
    </ThemeProvider>
  );
}

export default App;